// frontend/src/components/dashboard/ExpenseManager.jsx
import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, UsersIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify'; // ✅ ADD THIS IMPORT
import 'react-toastify/dist/ReactToastify.css'; // ✅ ADD THIS IMPORT (if not already added globally)
import groupDashboardService from '../../services/groupDashboardService';
import Button from '../common/Button';

const ExpenseManager = ({ groupId, membership, user }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false); // ✅ Add separate loading state for form
  
  const [newExpense, setNewExpense] = useState({
    title: '',
    amount: '',
    category: 'general',
    description: '',
    date: new Date().toISOString().split('T')[0] // ✅ Add date field
  });
  
  useEffect(() => {
    loadExpenses();
  }, [groupId]);
  
  const loadExpenses = async () => {
    try {
      setLoading(true);
      const [expensesData, summaryData] = await Promise.all([
        groupDashboardService.getExpenses(groupId),
        groupDashboardService.getExpenseSummary(groupId)
      ]);
      setExpenses(expensesData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddExpense = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newExpense.title.trim()) {
      toast.error('Please enter expense title');
      return;
    }
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      setFormLoading(true);
      
      const expenseData = {
        title: newExpense.title.trim(),
        amount: parseFloat(newExpense.amount),
        category: newExpense.category,
        description: newExpense.description.trim(),
        date: newExpense.date // Include date
      };
      
      await groupDashboardService.addExpense(groupId, expenseData);
      
      toast.success('Expense added successfully!');
      
      // Reset form
      setNewExpense({
        title: '',
        amount: '',
        category: 'general',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowAddForm(false);
      
      // Reload expenses
      loadExpenses();
      
    } catch (error) {
      console.error('Failed to add expense:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add expense';
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleSplitExpense = async (expenseId) => {
    if (!confirm('Split this expense equally among all members?')) {
      return;
    }
    
    try {
      await groupDashboardService.splitExpense(groupId, expenseId);
      toast.success('Expense split equally among members');
      loadExpenses();
    } catch (error) {
      console.error('Failed to split expense:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to split expense';
      toast.error(errorMessage);
    }
  };
  
  const handleDeleteExpense = async (expenseId) => {
    if (!confirm('Are you sure you want to delete this expense?')) {
      return;
    }
    
    try {
      await groupDashboardService.deleteExpense(groupId, expenseId);
      toast.success('Expense deleted successfully');
      loadExpenses();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete expense';
      toast.error(errorMessage);
    }
  };
  
  const categories = [
    { id: 'transport', name: 'Transport', color: 'bg-blue-100 text-blue-800' },
    { id: 'accommodation', name: 'Accommodation', color: 'bg-green-100 text-green-800' },
    { id: 'food', name: 'Food', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'activities', name: 'Activities', color: 'bg-purple-100 text-purple-800' },
    { id: 'shopping', name: 'Shopping', color: 'bg-pink-100 text-pink-800' },
    { id: 'emergency', name: 'Emergency', color: 'bg-red-100 text-red-800' },
    { id: 'general', name: 'General', color: 'bg-gray-100 text-gray-800' }
  ];
  
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading expenses...</p>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Expense Management</h2>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:shadow-md transition-shadow"
          disabled={formLoading}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          {showAddForm ? 'Cancel' : 'Add Expense'}
        </Button>
      </div>
      
      {/* Add Expense Form */}
      {showAddForm && (
        <div className="mb-8 p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl border border-gray-200 shadow-sm">
          <h3 className="font-bold text-xl text-gray-800 mb-6">Add New Expense</h3>
          <form onSubmit={handleAddExpense} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expense Title *
                </label>
                <input
                  type="text"
                  value={newExpense.title}
                  onChange={(e) => setNewExpense({ ...newExpense, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="e.g., Hotel Booking, Train Tickets"
                  required
                  disabled={formLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹) *
                </label>
                <input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="0.00"
                  step="0.01"
                  min="0.01"
                  required
                  disabled={formLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  disabled={formLoading}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Brief description or notes"
                  disabled={formLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  disabled={formLoading}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewExpense({
                    title: '',
                    amount: '',
                    category: 'general',
                    description: '',
                    date: new Date().toISOString().split('T')[0]
                  });
                }}
                className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors"
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all disabled:opacity-70"
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  'Add Expense'
                )}
              </Button>
            </div>
          </form>
        </div>
      )}
      
      {/* Expense Summary */}
      {summary && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-6 rounded-2xl shadow-sm">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-800">₹{summary.totalExpenses || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-6 rounded-2xl shadow-sm">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4">
                <UsersIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Per Person</p>
                <p className="text-2xl font-bold text-gray-800">
                  ₹{summary.membersCount ? Math.round((summary.totalExpenses || 0) / summary.membersCount) : 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 p-6 rounded-2xl shadow-sm">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-white font-bold text-lg">₹</span>
              </div>
              <div>
                <p className="text-sm text-gray-600">Recent Expenses</p>
                <p className="text-2xl font-bold text-gray-800">{summary.recentExpenses?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Expenses List */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">All Expenses ({expenses.length})</h3>
        </div>
        
        {expenses.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">No expenses yet</h4>
            <p className="text-gray-600 mb-6">Add your first expense to start tracking trip costs</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Expense
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expense
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{expense.title}</div>
                        {expense.description && (
                          <div className="text-sm text-gray-500 mt-1">{expense.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${categories.find(c => c.id === expense.category)?.color}`}>
                        {categories.find(c => c.id === expense.category)?.name || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">₹{expense.amount}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                          {expense.paidBy?.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{expense.paidBy?.name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">
                            {expense.createdBy === user._id ? 'You created this' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(expense.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleSplitExpense(expense._id)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Split equally among members"
                          disabled={formLoading}
                        >
                          <UsersIcon className="h-5 w-5" />
                        </button>
                        {expense.createdBy === user._id && (
                          <>
                            <button 
                              className="text-yellow-600 hover:text-yellow-800 transition-colors"
                              title="Edit expense"
                              disabled={formLoading}
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense._id)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Delete expense"
                              disabled={formLoading}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseManager;